import { useState } from 'react';
import dayjs from 'dayjs';
import { PaymentType } from '@bulthaup/api/db';
import { Button, NumberInput, Select, Stack } from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { IconDeviceFloppy } from '@tabler/icons-react';

import { handleFormError } from '../../../../../../utils/handle-form-error';
import { label } from '../../../../../../utils/label';

interface PaymentFormValues {
  amount: number | null;
  date: Date | null;
  type: PaymentType;
}

interface PaymentFormProps {
  onSubmit: (values: PaymentFormValues) => Promise<void>;
  initialValues?: PaymentFormValues;
}

export function PaymentForm({ onSubmit, initialValues }: PaymentFormProps) {
  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<PaymentFormValues>({
    initialValues: initialValues ?? {
      amount: null,
      date: null,
      type: PaymentType.ON_ORDER,
    },
    validate: {
      amount: value => (!value ? 'Le montant doit être supérieur à 0' : null),
      date: value => (!value ? 'La date est obligatoire' : null),
      type: value => (!value ? 'Le type est obligatoire' : null),
    },
  });

  const handleSubmit = async (values: PaymentFormValues) => {
    try {
      setIsLoading(true);
      const d = dayjs(values.date!).toDate();
      const utcDate = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
      await onSubmit({ ...values, date: utcDate });
    } catch (error) {
      handleFormError(form, error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <NumberInput
          label="Montant"
          required
          placeholder="0.00"
          decimalScale={2}
          thousandSeparator=" "
          min={0}
          suffix=" €"
          {...form.getInputProps('amount')}
        />

        <DateInput
          label="Date"
          required
          withWeekNumbers
          placeholder="Sélectionner une date"
          valueFormat="DD/MM/YYYY"
          clearable
          {...form.getInputProps('date')}
        />

        <Select
          label="Type de paiement"
          required
          data={Object.values(PaymentType)
            .map(type => ({
              value: type,
              label: label(type),
            }))
            .filter(
              (item): item is { value: PaymentType; label: string } =>
                item.label !== null,
            )}
          {...form.getInputProps('type')}
        />

        <Stack gap="xs">
          <Button
            type="submit"
            loading={isLoading}
            leftSection={<IconDeviceFloppy size={16} />}
          >
            {initialValues ? 'Modifier' : 'Ajouter'}
          </Button>
        </Stack>
      </Stack>
    </form>
  );
}
